import type { Container } from "@/types/container";
import type { Waypoint } from "@/components/application/map/route-map";

export const ROUTE_COLORS = [
    "#2563eb", // blue-600
    "#16a34a", // green-600
    "#db2777", // pink-600
    "#f59e0b", // amber-500
    "#7c3aed", // violet-600
    "#059669", // emerald-600
    "#dc2626", // red-600
    "#0ea5e9", // sky-500
];

export const POPUP_OFFSET_PX = 16;
export const POPUP_MAX_WIDTH_PX = 448; // max-w-md
export const LIST_PANEL_WIDTH_PX = 400;

/** Shared type for a route item (container + coordinates + color). */
export interface WorldMapRoute {
    c: Container;
    waypoints: Waypoint[];
    coordinates: [number, number][];
    from: [number, number];
    to: [number, number];
    color: string;
}

export function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

export function interpolate(a: [number, number], b: [number, number], t: number): [number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** Posizione nave lungo la rotta: progress in [0, 1]. Restituisce null se coordinate non valide. */
export function getShipPosition(coordinates: [number, number][], progress: number): [number, number] | null {
    if (!coordinates.length) return null;
    if (coordinates.length === 1) return coordinates[0];
    let totalDistance = 0;
    const segmentDistances: number[] = [];
    for (let i = 0; i < coordinates.length - 1; i++) {
        const dLat = coordinates[i + 1][0] - coordinates[i][0];
        const dLng = coordinates[i + 1][1] - coordinates[i][1];
        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
        segmentDistances.push(dist);
        totalDistance += dist;
    }
    if (totalDistance <= 0) return coordinates[0];
    const targetDistance = progress * totalDistance;
    let accumulatedDistance = 0;
    for (let i = 0; i < segmentDistances.length; i++) {
        const seg = segmentDistances[i];
        if (seg === 0) continue;
        if (accumulatedDistance + seg >= targetDistance) {
            const segmentT = (targetDistance - accumulatedDistance) / seg;
            return interpolate(coordinates[i], coordinates[i + 1], segmentT);
        }
        accumulatedDistance += seg;
        if (i === segmentDistances.length - 1) return coordinates[coordinates.length - 1];
    }
    return coordinates[coordinates.length - 1];
}

/** Bearing in degrees from north (0 = north, 90 = east). From segment [from, to]. */
export function bearingDegrees(from: [number, number], to: [number, number]): number {
    const dLng = to[1] - from[1];
    const dLat = to[0] - from[0];
    const deg = (Math.atan2(dLng, dLat) * 180) / Math.PI;
    return deg;
}

/** Cursor02 path from Untitled UI (viewBox 0 0 24 24, stroke icon). */
export const CURSOR02_PATH =
    'M20.506 10.775c.616-.24.925-.36 1.012-.53a.5.5 0 0 0-.006-.465c-.091-.168-.403-.28-1.026-.503L4.596 3.573c-.51-.183-.764-.275-.93-.217a.5.5 0 0 0-.31.309c-.057.167.034.421.217.931l5.704 15.89c.224.623.335.934.503 1.025a.5.5 0 0 0 .466.006c.17-.086.29-.395.53-1.012l2.596-6.677c.047-.121.07-.181.107-.232a.501.501 0 0 1 .117-.117c.05-.036.111-.06.232-.107l6.678-2.597Z';

export function getDocumentStatusColor(status: string): "success" | "warning" | "error" | "gray" {
    switch (status) {
        case 'Completo': return 'success';
        case 'Pending': return 'warning';
        case 'Error': return 'error';
        default: return 'gray';
    }
}
